import { Injectable, inject } from '@angular/core';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import * as CookieConsent from 'vanilla-cookieconsent';
import { Color, GsStructBorderRadius, GsStructBorderStyle, GsStructBorderWidth, ICompanyInfo, IUiButtonSettings } from '../../models';
import { ConfigService } from '../config/config.service';
import { DEFAULT_CONFIG } from '../config/default.config';
import { EnvironmentService } from '../environment/environment.service';
import { ConsentInfo, ConsentService } from './consent.service';

/** Consent revision — raise this whenever the service ids change, so stored consents are re-collected. */
const CONSENT_REVISION = 1;

/** Cookie the backend reads the visitor's consent from (`UserSession.CookiesAllowed`, `GMaps`, `Userway`, …). */
const BACKEND_CONSENT_COOKIE = 'gs.ocs.cookiemessageagreeed';
/** Value the backend stores when the visitor accepted none of the optional services. */
const BACKEND_CONSENT_DECLINED = 'false';

const BORDER_RADIUS_CSS: Record<GsStructBorderRadius, string> = {
  [GsStructBorderRadius.none]: '0',
  [GsStructBorderRadius.sm]: '0.125rem',
  [GsStructBorderRadius.regular]: '0.25rem',
  [GsStructBorderRadius.md]: '0.375rem',
  [GsStructBorderRadius.lg]: '0.5rem',
  [GsStructBorderRadius.xl]: '0.75rem',
  [GsStructBorderRadius.xxl]: '1rem',
  [GsStructBorderRadius.xxxl]: '1.5rem',
  [GsStructBorderRadius.full]: '9999px'
};

const BORDER_WIDTH_CSS: Record<GsStructBorderWidth, string> = {
  [GsStructBorderWidth.zeroPixel]: '0',
  [GsStructBorderWidth.onePixel]: '1px',
  [GsStructBorderWidth.twoPixels]: '2px',
  [GsStructBorderWidth.fourPixels]: '4px',
  [GsStructBorderWidth.eightPixels]: '8px',
  [GsStructBorderWidth.tenPixels]: '10px',
  [GsStructBorderWidth.twelvePixels]: '12px',
  [GsStructBorderWidth.fourteenPixels]: '14px',
  [GsStructBorderWidth.sixteenPixels]: '16px',
  [GsStructBorderWidth.eighteenPixels]: '18px',
  [GsStructBorderWidth.twentyPixels]: '20px'
};

const BORDER_STYLE_CSS: Record<GsStructBorderStyle, string> = {
  [GsStructBorderStyle.none]: 'none',
  [GsStructBorderStyle.solid]: 'solid',
  [GsStructBorderStyle.dotted]: 'dotted',
  [GsStructBorderStyle.dashed]: 'dashed',
  [GsStructBorderStyle.double]: 'double'
};

@Injectable()
export class CookiebannerService {
  // Services
  private readonly configService = inject(ConfigService);
  private readonly consentService = inject(ConsentService);
  private readonly cookieService = inject(SsrCookieService);
  private readonly env = inject(EnvironmentService);

  // Methods
  /**
   * Initializes the Cookie Consent configuration with various options, including GUI settings,
   * categories for different types of cookies, and consent handling.
   * @returns The Cookie Consent configuration object.
   */
  getCookieConsentConfig(
    companyInfo: ICompanyInfo,
    linkTargets: Record<string, string>,
    consentInfo: ConsentInfo
  ): CookieConsent.CookieConsentConfig {
    this.consentService.companyInfo.set(companyInfo);
    this.consentService.linkTargets.set(linkTargets);
    this.consentService.consentInfo.set(consentInfo);
    this.applyButtonStyleVariables();

    return {
      guiOptions: {
        consentModal: {
          layout: 'box wide',
          position: 'middle center',
          equalWeightButtons: true,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },
      categories: this.consentService.getCategories(),
      language: {
        default: this.consentService.defaultLanguage,
        autoDetect: 'browser',
        translations: this.consentService.getAllTranslations()
      },
      // Runs on every page load once a consent exists, so visitors who agreed before this was shipped
      // still get the backend cookie without reopening the banner
      onConsent: () => {
        this.syncBackendConsentCookie();
      },
      onChange: () => {
        this.syncBackendConsentCookie();
        window.location.reload();
      },
      onFirstConsent: () => {
        this.syncBackendConsentCookie();
        window.location.reload();
      },
      // Services are registered under their backend cookie name since CS4-2309 (matomo → matomostatistic, …).
      // vanilla-cookieconsent keeps the accepted service names verbatim from the stored cc_cookie, so a visitor
      // with an existing consent would keep the old ids forever and the guarded tracking snippets would stay
      // inactive. Bumping the revision invalidates those consents once and re-shows the banner.
      revision: CONSENT_REVISION,
      disablePageInteraction: true,
      hideFromBots: this.env.env?.ENVIRONMENT === 'production'
    };
  }

  /**
   * Writes the accepted services into the cookie the backend evaluates.
   * The backend gates its own consent-bound features (`UserSession.CookiesAllowed`, `GMaps`, `Userway`, …)
   * on this cookie, which the auth interceptor forwards as a request header — Cloud Software 4 runs
   * cross-origin, so the cookie itself never reaches the backend. Service ids already match the backend's
   * cookie names (see `ConsentService.getServiceId`), so the accepted ids can be written verbatim.
   */
  private syncBackendConsentCookie(): void {
    const preferences = CookieConsent.getUserPreferences();
    const acceptedServices = Object.values(preferences.acceptedServices ?? {}).flat();
    const value = acceptedServices.length > 0 ? acceptedServices.join(',') : BACKEND_CONSENT_DECLINED;
    this.cookieService.set(BACKEND_CONSENT_COOKIE, value, { expires: 365, path: '/', secure: true, sameSite: 'Lax' });
  }

  /**
   * Maps the configured button settings (defaults merged with the global config)
   * onto the `--cc-btn-*` CSS variables that vanilla-cookieconsent consumes.
   * Secondary buttons mirror the primary settings (per design decision).
   * Without a visible border (width 0 or style none) the 1px the plugin reserves is painted
   * in the background colors, matching the buttons rendered via ButtonStyleHelper.
   * Variables for unresolved settings are left unset so library defaults apply.
   */
  private applyButtonStyleVariables(): void {
    const settings: Partial<IUiButtonSettings> = {
      ...DEFAULT_CONFIG.uiElements?.buttons,
      ...this.configService.config()?.uiElements?.buttons
    };

    const root = document.documentElement;
    const setVar = (name: string, value: string | undefined): void => {
      if (value !== undefined) root.style.setProperty(name, value);
    };

    const hasBorder = this.hasBorder(settings);
    const bg = this.colorToCssValue(settings.backgroundColor);
    const text = this.colorToCssValue(settings.textColor);
    const hoverBg = this.colorToCssValue(settings.hoverBackgroundColor);
    const hoverText = this.colorToCssValue(settings.hoverTextColor);
    const border = hasBorder ? this.colorToCssValue(settings.borderColor) : bg;
    const hoverBorder = hasBorder ? this.colorToCssValue(settings.borderHoverColor) : hoverBg;

    setVar('--cc-btn-border-width', hasBorder ? BORDER_WIDTH_CSS[settings.borderWidth!] : '1px');
    setVar('--cc-btn-border-style', hasBorder ? BORDER_STYLE_CSS[settings.borderStyle!] : 'solid');

    setVar('--cc-btn-primary-bg', bg);
    setVar('--cc-btn-primary-color', text);
    setVar('--cc-btn-primary-border-color', border);
    setVar('--cc-btn-primary-hover-bg', hoverBg);
    setVar('--cc-btn-primary-hover-color', hoverText);
    setVar('--cc-btn-primary-hover-border-color', hoverBorder);

    setVar('--cc-btn-secondary-bg', bg);
    setVar('--cc-btn-secondary-color', text);
    setVar('--cc-btn-secondary-border-color', border);
    setVar('--cc-btn-secondary-hover-bg', hoverBg);
    setVar('--cc-btn-secondary-hover-color', hoverText);
    setVar('--cc-btn-secondary-hover-border-color', hoverBorder);

    const borderRadius = this.borderRadiusToCssValue(settings.borderRadius);
    setVar('--cc-btn-border-radius', borderRadius);
  }

  /**
   * Checks whether the settings describe a visible border (a width greater than zero and a style other than none).
   * @param settings Button styling settings to check
   * @returns True when the settings result in a border that takes up space
   */
  private hasBorder(settings: Partial<IUiButtonSettings>): boolean {
    return !!settings.borderWidth && settings.borderStyle != null && settings.borderStyle !== GsStructBorderStyle.none;
  }

  /**
   * Converts a Color (Tailwind token or HEX) into a CSS value.
   * Tailwind tokens reference the `--gs-<token>` runtime variable
   * that ConfigService writes to :root; HEX codes pass through verbatim.
   * @param color The configured color or undefined
   * @returns A CSS color string, or undefined when no color is set
   */
  private colorToCssValue(color: Color | undefined): string | undefined {
    if (!color) return undefined;
    if (color.startsWith('#')) return color;
    return `rgb(var(--gs-${color}) / 1)`;
  }
  /**
   * Maps a GsStructBorderRadius enum value to a CSS length that matches
   * the corresponding Tailwind `rounded-*` utility.
   * @param radius The configured radius or undefined
   * @returns The CSS length string, or undefined when no radius is set
   */
  private borderRadiusToCssValue(radius: GsStructBorderRadius | undefined): string | undefined {
    if (radius == null) return undefined;
    return BORDER_RADIUS_CSS[radius];
  }
}
